import { useState, useEffect, useLayoutEffect } from "react";
import "./Deck.css";
import { useSpring, animated, to } from "react-spring";
import { useBreakpointValue } from "@chakra-ui/react";
import { useGameContext } from "../../../context/gameContext/gameContext";

const PlayedCard = ({ url, startRect }) => {
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  const [containerHeight, setContainerHeight] = useState(window.innerHeight);
  const [animate, setAnimate] = useState(false);
  const [positionCalculated, setPositionCalculated] = useState(false);

  // Mirrors the --trick-size / --card-w custom properties in Deck.css so
  // the landing math below stays correct at every breakpoint instead of
  // assuming the desktop 300px trick / 80px card sizes.
  const trickSize = useBreakpointValue({ base: 140, sm: 190, md: 300 }) ?? 300;
  const defaultCardWidth =
    useBreakpointValue({ base: 40, sm: 54, md: 80 }) ?? 80;
  const defaultCardHeight =
    useBreakpointValue({ base: 55, sm: 74, md: 110 }) ?? 110;

  const { currentTurn } = useGameContext();

  // P2/P4's hands sit inside a rotated container, so getBoundingClientRect()
  // on a card there reports on-screen (rotated) width/height with the two
  // swapped relative to the card's natural, upright shape. Swap them back
  // so the overlay (which never itself rotates) doesn't render squished.
  const isRotatedSeat = currentTurn === "P2" || currentTurn === "P4";
  const cardWidth = startRect
    ? isRotatedSeat
      ? startRect.height
      : startRect.width
    : defaultCardWidth;
  const cardHeight = startRect
    ? isRotatedSeat
      ? startRect.width
      : startRect.height
    : defaultCardHeight;

  // Fallback used only if the real card element couldn't be found on
  // screen (shouldn't normally happen) - a rough per-seat guess so the
  // animation still has somewhere sane to start from.
  const calculateInitialPosition = () => {
    const vw = containerWidth;
    const vh = containerHeight;

    switch (currentTurn) {
      case "P3":
        return { x: vw / 2 - cardWidth / 2, y: 0 };
      case "P4":
        return { x: vw - cardWidth, y: vh / 2 - cardHeight / 2, rotate: -90 };
      case "P1":
        return { x: vw / 2 - cardWidth / 2, y: vh - cardHeight };
      case "P2":
        return { x: 0, y: vh / 2 - cardHeight / 2, rotate: 90 };
      default:
        return { x: 0, y: 0 };
    }
  };

  const [initialPosition, setInitialPosition] = useState(
    startRect
      ? { x: startRect.left, y: startRect.top }
      : calculateInitialPosition()
  );

  const rotatePlayedCard =
    currentTurn === "left" || currentTurn === "right" ? 0 : 0;

  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(window.innerWidth);
      setContainerHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Lands the card at its slot within the .trick pile (matching the
  // .P1C-.P4C CSS classes: P1=bottom, P2=left, P3=top, P4=right, each
  // centered on the cross axis) - expressed as a fraction of the actual
  // trick size and card dimensions rather than fixed pixels, so it stays
  // correct as --trick-size/--card-w shrink at smaller breakpoints.
  const trickHalf = trickSize / 2;
  const centerPosition = {
    x:
      containerWidth / 2 +
      (currentTurn === "P2"
        ? -trickHalf + 2
        : currentTurn === "P4"
        ? trickHalf - cardWidth - 2
        : -cardWidth / 2),
    y:
      containerHeight / 2 +
      (currentTurn === "P3"
        ? -trickHalf + 2
        : currentTurn === "P1"
        ? trickHalf - cardHeight - 2
        : -cardHeight / 2),
    rotate: rotatePlayedCard,
  };

  const animation = useSpring({
    from: initialPosition,
    to: centerPosition,
    config: { duration: 300 },
  });

  return (
    <animated.img
      src={url}
      style={{
        width: `${cardWidth}px`,
        height: `${cardHeight}px`,
        position: "absolute",
        left: to([animation.x], (x) => `${x}px`),
        top: to([animation.y], (y) => `${y}px`),
        transform: to([animation.rotate], (rotate) => `rotate(${rotate}deg)`),
      }}
    />
  );
};
export default PlayedCard;
